"use client"

import { TbAdjustments } from "react-icons/tb"

export type CustomizationProps = {
   activeConcept: string
   sqFt: number
   onSqFtChange: (value: number) => void
   activeMarketLevel: "low" | "medium" | "high"
   onMarketLevelChange: (level: "low" | "medium" | "high") => void
   secondGen: boolean
   onSecondGenChange: (value: boolean) => void
   usedEquip: boolean
   onUsedEquipChange: (value: boolean) => void
   alcohol: boolean
   onAlcoholChange: (value: boolean) => void
}

const toggleTrackClasses =
   "absolute inset-0 rounded-full bg-slate-200 transition-colors before:absolute before:left-[3px] before:bottom-[3px] before:h-4 before:w-4 before:rounded-full before:bg-white before:transition-transform before:content-[''] peer-checked:bg-[#f15a29] peer-checked:before:translate-x-[18px]"

export default function Customization({
   activeConcept,
   sqFt,
   onSqFtChange,
   activeMarketLevel,
   onMarketLevelChange,
   secondGen,
   onSecondGenChange,
   usedEquip,
   onUsedEquipChange,
   alcohol,
   onAlcoholChange,
}: CustomizationProps) {
   return (
      <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-9">
         <div className="mb-7 flex items-center gap-3 text-custom-2xl-bold ">
            <TbAdjustments className="h-6 w-6 text-custom-primary shrink-0" />
            Customize Design Framework
         </div>
         <div className="grid grid-cols-1 gap-6 md:grid-cols-[1.1fr_0.9fr] md:gap-10">
            <div>
               {activeConcept !== "food-truck" && (
                  <div className="mb-7">
                     <label className="mb-2.5 block text-custom-lg-medium">Square Footage Scaling</label>
                     <div className="flex items-center gap-4">
                        <input
                           type="range"
                           min="200"
                           max="12000"
                           step="100"
                           value={sqFt}
                           onChange={(e) => onSqFtChange(Number(e.target.value))}
                           className="w-full cursor-pointer accent-[#f15a29]"
                        />
                        <span className="flex min-w-[75px] flex-col items-center text-center text-custom-2xl-bold text-custom-primary">
                           <span>{sqFt.toLocaleString()}</span>
                           <span className="text-custom-sm-semibold">sf</span>
                        </span>
                     </div>
                  </div>
               )}
               <div>
                  <label className="mb-2.5 block text-custom-base-semibold">Market Cost Level</label>
                  <div className="flex rounded-md border border-slate-200 bg-slate-100 p-1">
                     {(["low", "medium", "high"] as const).map((level) => (
                        <button
                           key={level}
                           type="button"
                           onClick={() => onMarketLevelChange(level)}
                           className={`text-custom-sm-semibold flex-1 rounded px-3 py-2.5 transition-all ${
                              activeMarketLevel === level
                                 ? "bg-white font-bold text-[#f15a29] shadow-sm"
                                 : "hover:text-custom-primary"
                           }`}
                        >
                           {level}
                        </button>
                     ))}
                  </div>
               </div>
            </div>

            <div>
               <div className="mb-2.5 block text-custom-base-semibold">Cost-Saving Alternatives</div>
               <div className="flex flex-col gap-4">
                  {activeConcept !== "food-truck" && (
                     <div className="flex items-center gap-3.5">
                        <label className="relative inline-block h-[16px] w-[28px] shrink-0 cursor-pointer">
                           <input
                              type="checkbox"
                              checked={secondGen}
                              onChange={(e) => onSecondGenChange(e.target.checked)}
                              className="peer h-0 w-0 opacity-0"
                           />
                           <span className={toggleTrackClasses} />
                        </label>
                        <div className="flex flex-col leading-tight">
                           <span className="text-custom-sm-semibold">Second-gen space</span>
                           <span className="mt-0.5 text-custom-14-medium text-emerald-500">
                              Saves ~60% buildout infrastructure
                           </span>
                        </div>
                     </div>
                  )}

                  <div className="flex items-center gap-3.5">
                     <label className="relative inline-block h-[22px] w-[40px] shrink-0 cursor-pointer">
                        <input
                           type="checkbox"
                           checked={usedEquip}
                           onChange={(e) => onUsedEquipChange(e.target.checked)}
                           className="peer h-0 w-0 opacity-0"
                        />
                        <span className={toggleTrackClasses} />
                     </label>
                     <div className="flex flex-col leading-tight">
                        <span className="text-custom-sm-semibold">Used equipment</span>
                        <span className="mt-0.5 text-custom-14-medium text-emerald-500">
                           Saves ~45% on kitchen devices
                        </span>
                     </div>
                  </div>

                  <div className="flex items-center gap-3.5">
                     <label className="relative inline-block h-[22px] w-[40px] shrink-0 cursor-pointer">
                        <input
                           type="checkbox"
                           checked={alcohol}
                           onChange={(e) => onAlcoholChange(e.target.checked)}
                           className="peer h-0 w-0 opacity-0"
                        />
                        <span className={toggleTrackClasses} />
                     </label>
                     <div className="flex flex-col leading-tight">
                        <span className="text-custom-sm-semibold">Serving alcohol</span>
                        <span className="mt-0.5 text-custom-14-medium text-emerald-500">
                           Adds liquor licensing lines
                        </span>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </div>
   )
}
